/**
 * What a pane should do when its box may have changed.
 *
 * Separated from the pane for the reason `wheel.ts` gives about wheel verdicts:
 * the interesting cases cannot be reached from a test that mounts one. A pane
 * only announces over an open socket, and no harness here has one — jsdom has no
 * layout for a fit to change, and the e2e stub can never complete a terminal
 * handshake — so the decision would live in a branch nothing could ask about.
 *
 * There is **one** invariant underneath all of it: *the emulator's geometry and
 * the session's must not diverge*. tmux paints with absolute cursor addresses;
 * if the emulator is 90 columns while tmux believes it is 101, every line wraps
 * where tmux did not intend, each repaint lands a row lower than the last
 * instead of overwriting it, and the screen degrades into interleaved fragments.
 * That is why fitting and announcing are one decision and not two: a fit the
 * session is not told about is the divergence, deliberately created.
 *
 * The three answers:
 *
 *   - **skip** — nobody is looking at this window. A session can have more than
 *     one pane on it (the console open twice; measured on the deployment as ten
 *     terminal sockets for five visible tiles), and while nobody holds the input
 *     lease the server lets any of them size it. Two windows of different sizes
 *     would take turns, and whichever spoke last would leave the other rendering
 *     a screen it does not fit. Hidden is the tie-break: the window being read
 *     wins. And skipping means *both* halves — a hidden pane that re-fitted
 *     without announcing would create the divergence above rather than avoid it,
 *     which is exactly how this was got wrong once.
 *   - **fit** — the box changed but the size did not, or there is nothing to
 *     measure. Match the box; say nothing. A resize is not free on the far side:
 *     it raises SIGWINCH and tmux repaints every cell, which is a screenful of
 *     output indistinguishable from an agent at work, and the geometry is
 *     re-derived on every ten-second status frame.
 *   - **announce** — the size genuinely changed. Fit, and tell the session.
 *
 * What is deliberately *not* here: "the session reports a different size, so say
 * it again". That was tried (a91f1a7, reverted in da78986) and it is a resize
 * war between two panes with no tie-break — each correcting the other forever,
 * every correction a full repaint at a width the emulator is not at.
 */

export type GeometrySync = 'skip' | 'fit' | 'announce'

export interface GeometryClaim {
  /** `document.hidden` — is anybody looking at this pane's window? */
  hidden: boolean
  /** What this connection has already announced, or null if it has announced
   *  nothing. Cleared on connect, and again when the window returns to the
   *  front — at which point this pane's own size has very likely not changed
   *  while the *session's* has, and only clearing it lets the pane say so.
   *
   *  The rank is part of the record because it is part of the claim. Maximising
   *  a pane on a wall that holds a single tile changes the box by a few pixels
   *  and the rank completely; comparing sizes alone would call that a repeat and
   *  leave the server ranking this pane as a tile. */
  announced: { cols: number; rows: number; primary: boolean } | null
  /** Whether this pane is the whole view rather than one tile on a wall. The
   *  server ranks a maximised pane above the tiles: see sessionGeometry in
   *  internal/api/terminal_viewport.go for what that decides, and why a tile
   *  open on another machine must not hold this one at the tile's width. */
  primary: boolean
  /** What the fit would produce, asked before it is applied, so that one
   *  decision covers both halves. `undefined` when there is nothing to measure —
   *  a detached element mid-layout. */
  proposed: { cols: number; rows: number } | undefined
  /** What the emulator is right now. It is not always what the box would make
   *  it: a pane that has been following another client was resized to *their*
   *  geometry, so when they let go it is already at a size the session should be
   *  told about, measurable box or not. */
  current: { cols: number; rows: number }
}

export function geometrySync(claim: GeometryClaim): GeometrySync {
  if (claim.hidden) return 'skip'
  const { announced } = claim
  // What the emulator will be once this sync is done: the box's answer when
  // there is one, and otherwise what it already is. The fallback is not a
  // technicality — a pane that has been following another client is sitting at
  // *their* geometry, and when they let go that is the size the session has to
  // be told about whether or not the box could be measured this instant.
  const size = claim.proposed ?? claim.current
  if (
    announced &&
    announced.cols === size.cols &&
    announced.rows === size.rows &&
    announced.primary === claim.primary
  ) {
    return 'fit'
  }
  return 'announce'
}

/**
 * The size this pane must *render* at, or null to render at its own box.
 *
 * Announcing and rendering are two different questions and the file above only
 * answers the first. A pane announces what its box can display — a fact about
 * this window — and that never changes in response to the session. What it
 * renders at is the session's size, because that is the width tmux is painting
 * with, and the one invariant underneath all of this is that those two agree.
 *
 * Both halves are needed, and having only the first is what produced the report
 * this was written for: a session open in two windows was sized by whichever
 * spoke last, and the other window went on rendering at its own width. Out of
 * nowhere, from that operator's side — they had not touched anything, the other
 * window had — and corrected only by reloading the page, which re-announces and
 * wins the race again. The server now sizes a session from every viewer at once
 * (`sessionGeometry`, internal/api/terminal_viewport.go); this is the other end
 * of that, and without it the server's decision would be a number the pane
 * ignored.
 *
 * Why this is not the reverted a91f1a7. That change made a pane *announce* when
 * the session's reported geometry differed from its own, which is a feedback
 * loop: each pane corrects the other forever, every correction a full repaint.
 * This one only ever changes what is rendered. The announcement stays derived
 * from the box, so there is nothing for a second pane to react to.
 */
export interface AdoptionClaim {
  /** The session's own geometry from the status frame; zero when unknown. */
  session: { cols: number; rows: number }
  /** Whether somebody else holds input control. Their size wins outright: an
   *  observer must not reflow the terminal of the operator typing into it. */
  driven: boolean
  /** What this connection last told the server its box can display, or null on
   *  a fresh socket. */
  announced: { cols: number; rows: number } | null
}

export function adoptGeometry(claim: AdoptionClaim): { cols: number; rows: number } | null {
  const { session } = claim
  // Nothing to adopt. A session whose size is not known yet is not a session of
  // size zero, and rendering one at 0x0 is a blank pane with no way back.
  if (!(session.cols > 0 && session.rows > 0)) return null
  if (claim.driven) return { cols: session.cols, rows: session.rows }
  const { announced } = claim
  // This pane's own announcement is what the session is, so it is already the
  // right shape and should keep following its box — that is what lets it grow
  // again when a smaller viewer closes.
  if (announced && announced.cols === session.cols && announced.rows === session.rows) return null
  // Somebody else's window decided this, or the server clamped it to fit them.
  // Render what the session is; the box is only ever a claim about capacity.
  return { cols: session.cols, rows: session.rows }
}
